import { Stack, Pivot, PivotItem } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import styles from "./AnalysisPanel.module.css";

import { SupportingContent } from "../SupportingContent";
import { ChatAppResponse, getDevUsersFromPostgresqlApi } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { ThoughtProcess } from "./ThoughtProcess";
import { MarkdownViewer } from "../MarkdownViewer";
import { useMsal } from "@azure/msal-react";
import { getHeaders } from "../../api";
import { useLogin, getToken, getUsername } from "../../authConfig";
import { useState, useEffect } from "react";
import FileViewer from "react-file-viewer";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged }: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer.context.thoughts;
    const isDisabledSupportingContentTab: boolean = !answer.context.data_points;
    const isDisabledCitationTab: boolean = !activeCitation;
    const [citation, setCitation] = useState("");

    const isLogin = useLogin();
    const { instance } = useMsal();
    const client = isLogin ? instance : undefined;
    const { t } = useTranslation();

    const fetchCitation = async () => {
        const rawToken = client ? await getToken(client) : undefined;
        const token = rawToken === null ? undefined : rawToken;
        if (activeCitation) {
            // Get hash from the URL as it may contain #page=N
            // which helps browser PDF renderer jump to correct page N
            const originalHash = activeCitation.indexOf("#") ? activeCitation.split("#")[1] : "";
            const response = await fetch(activeCitation, {
                method: "GET",
                headers: getHeaders(token)
            });
            const citationContent = await response.blob();
            let citationObjectUrl = URL.createObjectURL(citationContent);
            // Add hash back to the new blob URL
            if (originalHash) {
                citationObjectUrl += "#" + originalHash;
            }
            setCitation(citationObjectUrl);
        }
    };
    useEffect(() => {
        fetchCitation();
    }, []);

    const renderFileViewer = () => {
        if (!activeCitation) {
            return null;
        }

        const fileExtension = activeCitation.split(".").pop()?.toLowerCase();
        switch (fileExtension) {
            case "png":
                return <img src={citation} className={styles.citationImg} alt="Citation Image" />;
            case "md":
                return <MarkdownViewer src={activeCitation} />;
            // case "doc":
            // case "docx":
            // case "xls":
            // case "xlsx":
            // case "ppt":
            // case "pptx":
            //     return <FileViewer fileType={fileExtension} filePath={citation} onError={(e: any) => console.error("Error displaying file", e)} />;

            // case "doc":
            // case "docx":
            // case "xls":
            // case "xlsx":
            // case "ppt":
            // case "pptx":
            //     return <FileViewer fileType={fileExtension} filePath={activeCitation} onError={(e: any) => console.error("Error displaying file", e)} />;
            default:
                return <iframe title="Citation" src={citation} width="100%" height={citationHeight} />;
        }
    };

    const [username, setUsername] = useState("");

    useEffect(() => {
        const fetchUsername = async () => {
            setUsername((await getUsername(instance)) ?? "");
        };
        fetchUsername();
    }, [instance]);

    const [devUsers, setDevUsers] = useState<string[]>([]); // Initialize as an empty array

    useEffect(() => {
        const fetchDevUsers = async () => {
            try {
                const users = await getDevUsersFromPostgresqlApi();
                setDevUsers(users);
            } catch (error) {
                console.error("Error fetching allowed users:", error);
                // Handle the error appropriately, e.g., display an error message to the user
                // or set a default list of allowed users.
                setDevUsers(["xxx.xx@vinci-energies.net"]); // Default user in case of error
            }
        };
        fetchDevUsers();
    }, []);

    return (
        <Pivot
            className={className}
            selectedKey={activeTab}
            onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}
        >
            {/* Afficher ou pas the thought process tab */}
            {devUsers.includes(username) && (
                <PivotItem
                    itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                    headerText={t("headerTexts.thoughtProcess")}
                    headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}
                >
                    <ThoughtProcess thoughts={answer.context.thoughts || []} />
                </PivotItem>
            )}

            {/* Afficher ou pas supporting content tab */}
            {devUsers.includes(username) && (
                <PivotItem
                    itemKey={AnalysisPanelTabs.SupportingContentTab}
                    headerText={t("headerTexts.supportingContent")}
                    headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}
                >
                    <SupportingContent supportingContent={answer.context.data_points} />
                </PivotItem>
            )}
            <PivotItem
                itemKey={AnalysisPanelTabs.CitationTab}
                headerText={t("headerTexts.citation")}
                headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}
            >
                {renderFileViewer()}
            </PivotItem>
        </Pivot>
    );
};
